<div class="auth-container">
  <div class="auth-background">
    <div class="gradient-orb gradient-orb-1"></div>
    <div class="gradient-orb gradient-orb-2"></div>
  </div>

  <div class="auth-content">
    <div class="auth-card">
      <!-- Top bar: Brand + Theme-Toggle -->
      <div class="desk-top">
        <div class="fp-brand">
          <img
            src="../../../../../assets/img/logo-text.svg"
            alt="FixPlaner"
            class="logo"
          />
        </div>
        <div class="auth-top-actions">
          <xds-icon-button
            [icon]="'fa-light ' + getThemeIconClass()"
            [ariaLabel]="'REG_THEME_TOGGLE_LABEL' | translate"
            (clicked)="toggleTheme()"
          ></xds-icon-button>
        </div>
      </div>

      <!-- Greeting — H1 + Lede (Eyebrow weg, Page-Zweck offensichtlich) -->
      <div class="desk-greet">
        <h1>
          {{ "CHANGE_PW_GREET_LINE_1" | translate }}
          <span class="grad">{{ "CHANGE_PW_GREET_BRAND" | translate }}</span>
        </h1>
        <p class="lede">{{ "CHANGE_PW_GREET_LEDE" | translate }}</p>
      </div>

      <!-- Form -->
      <form
        [formGroup]="newPasswordForm"
        autocomplete="off"
        (submit)="onSubmit()"
        class="auth-form xds-form-stack"
      >
        <!-- Hidden Email field for Password Manager -->
        <input
          type="email"
          formControlName="email"
          autocomplete="username email"
          style="display: none"
        />

        <!-- Old Password (hidden if pre-filled via @Input) -->
        <xds-form-field
          *ngIf="!isOldPasswordHidden"
          labelKey="OLD_PASSWORD"
          [required]="true"
          for="cp-old"
          [invalid]="validateControl('oldPassword')"
        >
          <xds-input
            id="cp-old"
            type="password"
            formControlName="oldPassword"
            [invalid]="validateControl('oldPassword')"
            placeholderKey="ENTER_OLD_PASSWORD"
            testId="change-password-old"
            autocomplete="current-password"
          ></xds-input>
        </xds-form-field>

        <!-- New Password -->
        <xds-form-field
          labelKey="NEW_PASSWORD"
          [required]="true"
          for="cp-new"
          hintKey="REG_PW_LABEL_HINT"
          hintIcon="fa-light fa-circle-info"
        >
          <xds-input
            id="cp-new"
            [type]="showPassword ? 'text' : 'password'"
            formControlName="password"
            [invalid]="validateControl('password')"
            placeholderKey="ENTER_NEW_PASSWORD"
            testId="change-password-new"
            autocomplete="new-password"
          >
            <ng-container ngProjectAs="[xdsInputTrailing]">
              <xds-icon-button
                [icon]="showPassword ? 'fa-light fa-eye-slash' : 'fa-light fa-eye'"
                [ariaLabel]="(showPassword ? 'HIDE_PASSWORD' : 'SHOW_PASSWORD') | translate"
                (clicked)="toggleShowPassword()"
              ></xds-icon-button>
            </ng-container>
          </xds-input>
        </xds-form-field>

        <!-- Confirm Password -->
        <xds-form-field
          labelKey="CONFIRM_NEW_PASSWORD"
          [required]="true"
          for="cp-confirm"
          [invalid]="isConfirmPasswordInvalid()"
          errorKey="PASSWORDS MUST MATCH"
        >
          <xds-input
            id="cp-confirm"
            [type]="showConfirmPassword ? 'text' : 'password'"
            formControlName="confirmPassword"
            [invalid]="isConfirmPasswordInvalid()"
            placeholderKey="REG_PW_REPEAT_PLACEHOLDER"
            autocomplete="new-password"
          >
            <ng-container ngProjectAs="[xdsInputTrailing]">
              <xds-icon-button
                [icon]="showConfirmPassword ? 'fa-light fa-eye-slash' : 'fa-light fa-eye'"
                [ariaLabel]="(showConfirmPassword ? 'HIDE_PASSWORD' : 'SHOW_PASSWORD') | translate"
                (clicked)="toggleShowConfirmPassword()"
              ></xds-icon-button>
            </ng-container>
          </xds-input>
        </xds-form-field>

        <!-- Password strength block -->
        <div
          class="pw-block"
          *ngIf="newPasswordForm.get('password')?.value"
        >
          <div
            class="pw-bars"
            [attr.data-strength]="getPasswordStrengthData()"
            aria-hidden="true"
          >
            <div class="seg" [class.on]="passwordStrength >= 1"></div>
            <div class="seg" [class.on]="passwordStrength >= 2"></div>
            <div class="seg" [class.on]="passwordStrength >= 3"></div>
            <div class="seg" [class.on]="passwordStrength >= 4"></div>
            <div class="seg" [class.on]="passwordStrength >= 5"></div>
          </div>
          <div class="pw-line" aria-live="polite">
            <span class="pw-status">
              <span class="ico"><i class="fa-light fa-shield-halved"></i></span>
              <span>{{ "REG_PW_STRENGTH_LABEL" | translate }}</span>
            </span>
            <span class="strong" *ngIf="passwordStrength === 5">
              <span class="ico-ok"><i class="fa-solid fa-check"></i></span>
              {{ getPasswordStrengthLabel() | translate }} · {{ passwordStrength }} / 5
            </span>
            <span class="level" *ngIf="passwordStrength < 5">
              {{ getPasswordStrengthLabel() | translate }} · {{ passwordStrength }} / 5
            </span>
          </div>
          <div class="pw-checks">
            <span class="chk" [class.ok]="!hasError('password', 'minlength') && newPasswordForm.get('password')?.value">
              <i class="fa-light fa-check"></i>{{ "REG_PW_CHK_LENGTH" | translate }}
            </span>
            <span class="chk" [class.ok]="!hasError('password', 'missingUpper') && newPasswordForm.get('password')?.value">
              <i class="fa-light fa-check"></i>{{ "REG_PW_CHK_UPPER" | translate }}
            </span>
            <span class="chk" [class.ok]="!hasError('password', 'missingLower') && newPasswordForm.get('password')?.value">
              <i class="fa-light fa-check"></i>{{ "REG_PW_CHK_LOWER" | translate }}
            </span>
            <span class="chk" [class.ok]="!hasError('password', 'missingDigit') && newPasswordForm.get('password')?.value">
              <i class="fa-light fa-check"></i>{{ "REG_PW_CHK_DIGIT" | translate }}
            </span>
            <span class="chk" [class.ok]="!hasError('password', 'missingSpecial') && newPasswordForm.get('password')?.value">
              <i class="fa-light fa-check"></i>{{ "REG_PW_CHK_SPECIAL" | translate }}
            </span>
          </div>
        </div>

        <!-- Actions -->
        <div class="desk-actions">
          <xds-button
            type="submit"
            variant="primary"
            iconRight="fa-light fa-check"
            [disabled]="!newPasswordForm.valid"
            data-testid="change-password-submit"
          >
            {{ "UPDATE_PASSWORD" | translate }}
          </xds-button>
          <p class="helper">
            <a [routerLink]="['/dashboard']">
              <i class="fa-light fa-arrow-left"></i>
              {{ "BACK_TO_DASHBOARD" | translate }}
            </a>
          </p>
        </div>
      </form>
    </div>
  </div>
</div>
